import { z } from 'zod';
import { CarAdmissionType } from '@/types/car/admission-type.type.ts';
import { CarBodyType } from '@/types/car/car-body-type.type.ts';
import { CarEngineType } from '@/types/car/car-engine-type.type.ts';
import { CarNdsType } from '@/types/car/car-nds-type.ts';
import { CarTransmissionType } from '@/types/car/car-transmission-type.type.ts';
import {
    CarDamageCreateForm,
    CarDamageCreateFormErrors,
    CarDamageCreateFormErrorsSchema,
} from '@/types/car/car-damage-create-form.type.ts';
import {
    ComplectationOptionsCreateForm,
    ComplectationOptionsCreateFormErrors,
    ComplectationOptionsCreateFormErrorsSchema,
} from '@/types/complectation.type.ts';
import { CarDriveType } from '@/types/car/car-drive-type.type.ts';
import { CarPhotoType } from '@/types/car/car-photo-type.type.ts';
import { CarDocumentType } from '@/types/car/car-document-type.type.ts';

export type CarCreateFormPhoto = {
    file?: File | null,
    rotation?: number,
};

export type CarCreateFormPhotos = {
    [P in CarPhotoType]?: CarCreateFormPhoto
};

export type CarCreateFormDocuments = {
    [P in CarDocumentType]?: Array<File | null>
};

export type CarCreateForm = {
    brand_id?: number,
    model_id?: number,
    model_code_id?: number,
    vin: string,
    admission_type?: CarAdmissionType,
    pts_number: string,
    body_type?: CarBodyType,
    body_color: string,
    engine_type?: CarEngineType,
    drive_type?: CarDriveType,
    mileage?: number,
    production_year?: number,
    engine_power?: number,
    engine_volume?: number,
    start_price?: number,
    nds_type?: CarNdsType,
    transmission_type?: CarTransmissionType,
    additional_equipment?: string,
    dealer_id?: number,
    complectation_id?: number,
    owners_count?: number,
    months_with_last_owner?: number,
    is_stolen?: 0 | 1,
    is_pledged?: 0 | 1,
    exist_dtp?: 0 | 1,
    comment?: string,
    tags?: string,
    was_owned_by_individual?: 0 | 1,
    options?: ComplectationOptionsCreateForm,
    car_damages?: CarDamageCreateForm[],
    photos?: CarCreateFormPhotos,
    additional_photos?: CarCreateFormPhoto[],
    documents?: CarCreateFormDocuments,
    car_videos?: File[],
};

export type CarCreateFormPhotoErrors = {
    file?: string[],
    rotation?: string[],
};

export type CarCreateFormErrors = {
    brand_id?: string[],
    model_id?: string[],
    model_code_id?: string[],
    vin?: string[],
    admission_type?: string[],
    pts_number?: string[],
    body_type?: string[],
    body_color?: string[],
    engine_type?: string[],
    drive_type?: string[],
    mileage?: string[],
    production_year?: string[],
    engine_power?: string[],
    engine_volume?: string[],
    start_price?: string[],
    nds_type?: string[],
    transmission_type?: string[],
    additional_equipment?: string[],
    dealer_id?: string[],
    complectation_id?: string[],
    owners_count?: string[],
    months_with_last_owner?: string[],
    is_stolen?: string[],
    is_pledged?: string[],
    exist_dtp?: string[],
    comment?: string[],
    tags?: string[],
    was_owned_by_individual?: string[],
    options?: ComplectationOptionsCreateFormErrors,
    car_damages?: (CarDamageCreateFormErrors | undefined)[],
    photos?: Record<CarPhotoType, CarCreateFormPhotoErrors>,
    additional_photos?: (CarCreateFormPhotoErrors | undefined)[],
    documents?: Record<CarDocumentType, (string[] | undefined)[]>,
    car_videos?: (string[] | undefined)[],
};

export const CarCreateFormErrorsMessages = {
    required: 'Поле обязательно для заполнения',
    vinDecodingRequired: 'Необходимо декодировать VIN',
    numeric: 'Поле должно быть числом',
    integer: 'Поле должно быть целым числом',
};

export function mapCarCreateFormToFormData(createForm: CarCreateForm): FormData {
    const formData = new FormData();

    if (createForm.brand_id !== undefined) {
        formData.append('brand_id', `${createForm.brand_id}`);
    }

    if (createForm.model_id !== undefined) {
        formData.append('model_id', `${createForm.model_id}`);
    }

    if (createForm.model_code_id !== undefined) {
        formData.append('model_code_id', `${createForm.model_code_id}`);
    }

    if (createForm.vin.length > 0) {
        formData.append('vin', createForm.vin);
    }

    if (createForm.admission_type !== undefined) {
        formData.append('admission_type', createForm.admission_type);
    }

    if (createForm.pts_number.length > 0) {
        formData.append('pts_number', createForm.pts_number);
    }

    if (createForm.body_type !== undefined) {
        formData.append('body_type', createForm.body_type);
    }

    if (createForm.body_color.length > 0) {
        formData.append('body_color', createForm.body_color);
    }

    if (createForm.engine_type !== undefined) {
        formData.append('engine_type', createForm.engine_type);
    }

    if (createForm.drive_type !== undefined) {
        formData.append('drive_type', createForm.drive_type);
    }

    if (createForm.mileage !== undefined) {
        formData.append('mileage', `${createForm.mileage}`);
    }

    if (createForm.production_year !== undefined) {
        formData.append('production_year', `${createForm.production_year}`);
    }

    if (createForm.engine_power !== undefined) {
        formData.append('engine_power', `${createForm.engine_power}`);
    }

    if (createForm.engine_volume !== undefined) {
        formData.append('engine_volume', `${createForm.engine_volume}`);
    }

    if (createForm.start_price !== undefined) {
        formData.append('start_price', `${createForm.start_price}`);
    }

    if (createForm.nds_type !== undefined) {
        formData.append('nds_type', createForm.nds_type);
    }

    if (createForm.transmission_type !== undefined) {
        formData.append('transmission_type', createForm.transmission_type);
    }

    if (createForm.additional_equipment !== undefined && createForm.additional_equipment.length > 0) {
        formData.append('additional_equipment', createForm.additional_equipment);
    }

    if (createForm.dealer_id !== undefined) {
        formData.append('dealer_id', `${createForm.dealer_id}`);
    }

    if (createForm.complectation_id !== undefined) {
        formData.append('complectation_id', `${createForm.complectation_id}`);
    }

    if (createForm.owners_count !== undefined) {
        formData.append('owners_count', `${createForm.owners_count}`);
    }

    if (createForm.months_with_last_owner !== undefined) {
        formData.append('months_with_last_owner', `${createForm.months_with_last_owner}`);
    }

    if (createForm.is_stolen !== undefined) {
        formData.append('is_stolen', `${createForm.is_stolen}`);
    }

    if (createForm.is_pledged !== undefined) {
        formData.append('is_pledged', `${createForm.is_pledged}`);
    }

    if (createForm.exist_dtp !== undefined) {
        formData.append('exist_dtp', `${createForm.exist_dtp}`);
    }

    if (createForm.comment !== undefined && createForm.comment.length > 0) {
        formData.append('comment', createForm.comment);
    }

    if (createForm.tags !== undefined && createForm.tags.length > 0) {
        formData.append('tags', createForm.tags);
    }

    if (createForm.was_owned_by_individual !== undefined) {
        formData.append('was_owned_by_individual', `${createForm.was_owned_by_individual}`);
    }

    if (createForm.options !== undefined) {
        Object.entries(createForm.options).forEach(([key, value]) => {
            formData.append(`options[${key}]`, value);
        });
    }

    if (createForm.car_damages !== undefined) {
        createForm.car_damages.forEach((damage: CarDamageCreateForm, index: number) => {
            formData.append(`car_damages[${index}][symbolic_code]`, damage.symbolic_code ?? '');
            formData.append(`car_damages[${index}][type]`, damage.type ?? '');

            if (damage.photo !== undefined) {
                formData.append(`car_damages[${index}][photo]`, damage.photo);
            }

            if (damage.paintwork_thickness !== undefined) {
                formData.append(`car_damages[${index}][paintwork_thickness]`, `${damage.paintwork_thickness}`);
            }

            if (damage.comment !== undefined && damage.comment.length > 0) {
                formData.append(`car_damages[${index}][comment]`, `${damage.comment}`);
            }
        });
    }

    if (createForm.photos !== undefined) {
        Object.entries(createForm.photos).forEach(([type, photo]) => {
            if (photo.file !== undefined && photo.file !== null) {
                formData.append(`photos[${type}][file]`, photo.file);
            }

            if (photo.rotation !== undefined) {
                formData.append(`photos[${type}][rotation]`, `${photo.rotation}`);
            }
        });
    }

    if (createForm.additional_photos !== undefined) {
        createForm.additional_photos.forEach((photo: CarCreateFormPhoto, index: number) => {
            if (photo.file !== undefined && photo.file !== null) {
                formData.append(`additional_photos[${index}][file]`, photo.file);
            }

            if (photo.rotation !== undefined) {
                formData.append(`additional_photos[${index}][rotation]`, `${photo.rotation}`);
            }
        });
    }

    if (createForm.documents !== undefined) {
        Object.entries(createForm.documents).forEach(([type, files]) => {
            files.forEach((file: File | null, index: number) => {
                if (file !== null) {
                    formData.append(`documents[${type}][${index}]`, file);
                }
            });
        });
    }

    if (createForm.car_videos !== undefined) {
        createForm.car_videos.forEach((file: File, index: number) => {
            formData.append(`car_videos[${index}]`, file);
        });
    }

    return formData;
}

export const CarCreateFormErrorsSchema = z.object({
    brand_id: z.array(z.string()).optional(),
    model_id: z.array(z.string()).optional(),
    model_code_id: z.array(z.string()).optional(),
    vin: z.array(z.string()).optional(),
    admission_type: z.array(z.string()).optional(),
    pts_number: z.array(z.string()).optional(),
    body_type: z.array(z.string()).optional(),
    body_color: z.array(z.string()).optional(),
    engine_type: z.array(z.string()).optional(),
    drive_type: z.array(z.string()).optional(),
    mileage: z.array(z.string()).optional(),
    production_year: z.array(z.string()).optional(),
    engine_power: z.array(z.string()).optional(),
    engine_volume: z.array(z.string()).optional(),
    start_price: z.array(z.string()).optional(),
    nds_type: z.array(z.string()).optional(),
    transmission_type: z.array(z.string()).optional(),
    additional_equipment: z.array(z.string()).optional(),
    dealer_id: z.array(z.string()).optional(),
    complectation_id: z.array(z.string()).optional(),
    owners_count: z.array(z.string()).optional(),
    months_with_last_owner: z.array(z.string()).optional(),
    is_stolen: z.array(z.string()).optional(),
    is_pledged: z.array(z.string()).optional(),
    exist_dtp: z.array(z.string()).optional(),
    comment: z.array(z.string()).optional(),
    tags: z.array(z.string()).optional(),
    was_owned_by_individual: z.array(z.string()).optional(),
    options: ComplectationOptionsCreateFormErrorsSchema.optional(),
    car_damages: z.array(CarDamageCreateFormErrorsSchema.optional()).optional(),
    photos: z.record(z.nativeEnum(CarPhotoType), z.object({
        file: z.string().array().optional(),
        rotation: z.string().array().optional(),
    }).optional()).optional(),
    additional_photos: z.array(z.object({
        file: z.string().array().optional(),
        rotation: z.string().array().optional(),
    }).optional()).optional(),
    documents: z.record(z.nativeEnum(CarDocumentType), z.array(z.string().array().optional())).optional(),
    car_videos: z.array(z.string().array().optional()).optional(),
});
